import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const SEV_MAP = {
  info:    { bg: '#dbeafe', color: '#1d4ed8', icon: 'bi-info-circle-fill' },
  success: { bg: '#dcfce7', color: '#166534', icon: 'bi-check-circle-fill' },
  warning: { bg: '#fef9c3', color: '#854d0e', icon: 'bi-exclamation-triangle-fill' },
  error:   { bg: '#fee2e2', color: '#991b1b', icon: 'bi-x-octagon-fill' },
};

function timeAgo(ts) {
  const d = (Date.now() - new Date(ts)) / 1000;
  if (d < 60) return `${Math.floor(d)}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return new Date(ts).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function SANotifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread | info | success | warning | error
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: PAGE_SIZE };
      if (filter !== 'all' && ['info', 'success', 'warning', 'error'].includes(filter)) params.severity = filter;
      if (filter === 'unread') params.unread = true;
      const res = await api.get('/sa/notifications', { params });
      const data = res.data.data;
      setItems(data?.notifications || data || []);
      setTotal(data?.total ?? (data?.length ?? 0));
    } catch (_) {}
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { setPage(1); load(1); }, [filter, load]);

  const markRead = async (id) => {
    try { await api.patch(`/sa/notifications/${id}/read`); } catch (_) {}
    setItems(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
  };

  const markAllRead = async () => {
    try { await api.patch('/sa/notifications/read-all'); } catch (_) {}
    setItems(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const del = async (id) => {
    try { await api.delete(`/sa/notifications/${id}`); } catch (_) {}
    setItems(prev => prev.filter(n => n._id !== id));
    setTotal(t => Math.max(0, t - 1));
  };

  const unreadCount = items.filter(n => !n.isRead).length;

  const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'info', label: 'Info' },
    { key: 'success', label: 'Success' },
    { key: 'warning', label: 'Warning' },
    { key: 'error', label: 'Error' },
  ];

  return (
    <div style={{ maxWidth: 900 }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h4 className="mb-0 fw-bold" style={{ color: '#0f172a', letterSpacing: '-0.02em' }}>
            <i className="bi bi-bell-fill me-2 text-primary" />Platform Notifications
          </h4>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            All activity events from tenants and platform operations
          </div>
        </div>
        {unreadCount > 0 && (
          <button className="btn btn-sm" onClick={markAllRead} style={{ background: '#ede9fe', color: '#6366f1', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13 }}>
            <i className="bi bi-check-all me-1" />Mark all read
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{ padding: '5px 14px', borderRadius: 20, border: '1.5px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
              background: filter === f.key ? '#6366f1' : '#fff',
              color: filter === f.key ? '#fff' : '#64748b',
              borderColor: filter === f.key ? '#6366f1' : '#e2e8f0' }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
        {loading ? (
          <div className="p-4 text-center"><LoadingSpinner text="Loading notifications…" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-5" style={{ color: '#94a3b8' }}>
            <i className="bi bi-bell-slash" style={{ fontSize: 36, display: 'block', marginBottom: 10 }} />
            <div style={{ fontSize: 14 }}>No notifications found</div>
          </div>
        ) : (
          <div>
            {items.map((n, idx) => {
              const sev = SEV_MAP[n.severity] || SEV_MAP.info;
              return (
                <div key={n._id}
                  style={{ padding: '14px 20px', borderBottom: idx < items.length - 1 ? '1px solid #f1f5f9' : 'none', background: n.isRead ? '#fff' : '#fdfcff', transition: 'background 0.2s' }}>
                  <div className="d-flex gap-3 align-items-start">
                    {/* Icon */}
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: sev.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={`bi ${sev.icon}`} style={{ fontSize: 16, color: sev.color }} />
                    </div>
                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="d-flex align-items-start justify-content-between gap-2">
                        <div>
                          <div style={{ fontWeight: n.isRead ? 500 : 700, color: '#0f172a', fontSize: 13, lineHeight: 1.35 }}>{n.title}</div>
                          {n.message && <div style={{ color: '#64748b', fontSize: 12, marginTop: 3, lineHeight: 1.4 }}>{n.message}</div>}
                        </div>
                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                          {!n.isRead && (
                            <button onClick={() => markRead(n._id)} title="Mark read"
                              style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: '#ede9fe', color: '#6366f1', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                              <i className="bi bi-check-lg" />
                            </button>
                          )}
                          <button onClick={() => del(n._id)} title="Delete"
                            style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: '#fee2e2', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3 mt-1" style={{ fontSize: 11, color: '#94a3b8', flexWrap: 'wrap' }}>
                        <span><i className="bi bi-clock me-1" />{timeAgo(n.createdAt)}</span>
                        {n.tenantName && <span><i className="bi bi-building me-1" />{n.tenantName}</span>}
                        {n.triggeredBy && <span><i className="bi bi-person me-1" />{n.triggeredBy}</span>}
                        <span style={{ background: sev.bg, color: sev.color, padding: '1px 8px', borderRadius: 10, fontWeight: 600, fontSize: 10.5 }}>
                          {n.severity}
                        </span>
                        {!n.isRead && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#6366f1', display: 'inline-block' }} />}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="d-flex justify-content-between align-items-center mt-3 px-1" style={{ fontSize: 13 }}>
          <span style={{ color: '#64748b' }}>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}</span>
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => { setPage(p => p - 1); load(page - 1); }} style={{ borderRadius: 7 }}>
              <i className="bi bi-chevron-left" />
            </button>
            <button className="btn btn-sm btn-outline-secondary" disabled={page * PAGE_SIZE >= total} onClick={() => { setPage(p => p + 1); load(page + 1); }} style={{ borderRadius: 7 }}>
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
